import { type PostContent as LinkedInPostContent } from '@backend/lib/platforms/linkedin'
import { type PostContent as InstagramPostContent } from '@backend/lib/platforms/instagram'
import { type PostContent as TikTokPostContent } from '@backend/lib/platforms/tiktok'
import { type PostContent as YouTubePostContent } from '@backend/lib/platforms/youtube'
import { type PostContent as FacebookPostContent } from '@backend/lib/platforms/facebook'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'
import { WorkspacePreviewInstagram } from './workspace-preview-instagram'
import { WorkspacePreviewLinkedin } from './workspace-preview-linkedin'
import { WorkspacePreviewTiktok } from './workspace-preview-tiktok'
import { WorkspacePreviewYoutube } from './workspace-preview-youtube'
import { WorkspacePreviewFacebook } from './workspace-preview-facebook'
import { type SelectedAccount } from '../workspace-header/workspace-header-social-accounts'

interface Props {
  showPreview: boolean
  selectedPlatform: PlatformType
  currentAccount: SelectedAccount | undefined
  currentPostContent:
    | LinkedInPostContent
    | InstagramPostContent
    | TikTokPostContent
    | YouTubePostContent
    | FacebookPostContent
  postAsCarousel?: boolean
  carouselImageUrls?: string[]
  youtubePostAsShort?: boolean
  /** Whether this platform will publish with a visible AI label baked into the
   *  image. Drawn into the preview so the user approves what actually ships;
   *  baking is irreversible on the published post. YouTube is video-only and
   *  never carries one. */
  showAiLabel?: boolean
  /** Appearance of the baked label. */
  aiLabelStyle?: AiLabelStyle
}

export const WorkspacePreviewContent = ({
  showPreview,
  selectedPlatform,
  currentAccount,
  currentPostContent,
  postAsCarousel = false,
  carouselImageUrls,
  youtubePostAsShort = false,
  showAiLabel = false,
  aiLabelStyle,
}: Props) => {
  if (!showPreview) return null

  return (
    <div className="overflow-y-auto px-4 pb-[70px]">
      <div className="mx-auto max-w-[400px] sm:max-w-[450px]">
        <div className="shadow-xs rounded-lg border border-gray-200 bg-white">
          {selectedPlatform === 'instagram' && (
            <WorkspacePreviewInstagram
              account={
                currentAccount
                  ? {
                      id: currentAccount.accountId,
                      displayName: currentAccount.displayName,
                      imageUrl: currentAccount.imageUrl,
                      subline: undefined,
                    }
                  : undefined
              }
              postContent={currentPostContent as InstagramPostContent}
              _onStartAuth={() => {}}
              showAiLabel={showAiLabel}
              aiLabelStyle={aiLabelStyle}
            />
          )}
          {selectedPlatform === 'linkedin' && (
            <WorkspacePreviewLinkedin
              account={
                currentAccount
                  ? {
                      id: currentAccount.accountId,
                      displayName: currentAccount.displayName,
                      imageUrl: currentAccount.imageUrl,
                      subline: undefined,
                    }
                  : undefined
              }
              postContent={currentPostContent as LinkedInPostContent}
              _onStartAuth={() => {}}
              postAsCarousel={postAsCarousel}
              carouselImageUrls={carouselImageUrls}
              showAiLabel={showAiLabel}
              aiLabelStyle={aiLabelStyle}
            />
          )}
          {selectedPlatform === 'tiktok' && (
            <WorkspacePreviewTiktok
              account={
                currentAccount
                  ? {
                      id: currentAccount.accountId,
                      displayName: currentAccount.displayName,
                      imageUrl: currentAccount.imageUrl,
                      subline: undefined,
                    }
                  : undefined
              }
              postContent={currentPostContent as TikTokPostContent}
              _onStartAuth={() => {}}
              showAiLabel={showAiLabel}
              aiLabelStyle={aiLabelStyle}
            />
          )}
          {selectedPlatform === 'youtube' && (
            <WorkspacePreviewYoutube
              account={
                currentAccount
                  ? {
                      id: currentAccount.accountId,
                      displayName: currentAccount.displayName,
                      imageUrl: currentAccount.imageUrl,
                      subline: undefined,
                    }
                  : undefined
              }
              postContent={currentPostContent as YouTubePostContent}
              postAsShort={youtubePostAsShort}
              _onStartAuth={() => {}}
            />
          )}
          {selectedPlatform === 'facebook' && (
            <WorkspacePreviewFacebook
              account={
                currentAccount
                  ? {
                      id: currentAccount.accountId,
                      displayName: currentAccount.displayName,
                      imageUrl: currentAccount.imageUrl,
                      subline: undefined,
                    }
                  : undefined
              }
              postContent={currentPostContent as FacebookPostContent}
              _onStartAuth={() => {}}
              showAiLabel={showAiLabel}
              aiLabelStyle={aiLabelStyle}
            />
          )}
        </div>
      </div>
    </div>
  )
}
